// Compose-and-send for one follow-up email. Mirrors SendAcceptanceEmailModal:
// fixed overlay, navy header, editable subject/body, sends through the shared
// email endpoint and closes on success.
import { useState } from "react";
import { X, Send, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { sendFollowUpEmail } from "../../utils/emailSender";
import { wrapBrandedEmail, plainTextToHtml } from "../../utils/emailBrandTemplate";

export default function FollowUpEmailModal({
  sender, toEmail, bcc, toName, initialSubject, initialBody, touchKey, dealId, propertyAddress, onClose, onSent,
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (sending) return;
    setSending(true);
    const html = wrapBrandedEmail({
      contentHtml: plainTextToHtml(body),
      sender,
      propertyAddress,
      // Past-client mail, not an active transaction — the default footer
      // line would be wrong here.
      footerText: "You're receiving this because you closed on your home with Team Edelstein.",
    });
    const res = await sendFollowUpEmail({
      userId: sender?.id,
      to: toEmail,
      bcc,
      toName,
      subject,
      text: body,
      html,
      touchKey,
      relatedDealId: dealId,
    });
    setSending(false);
    if (res.ok) {
      toast.success("Check-in sent");
      onSent();
    } else {
      toast.error(res.error || "Could not send");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between">
            <div className="text-base font-bold flex items-center gap-2">
              <Mail size={16} /> Check-in email · {toName}
            </div>
            <button onClick={onClose} className="p-1 text-slate-300 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-3">
            <div className="text-xs text-gray-500">To: {toEmail}</div>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-44"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">
                Cancel
              </button>
              <button
                onClick={send}
                disabled={sending || !toEmail}
                className="inline-flex items-center gap-2 bg-[#1E293B] hover:bg-slate-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                <Send size={14} /> {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
